<template>
    <div class="main-content main-content_vertical" v-if="!$store.getters.cryptocurrencyPaymentMode">
        <div class="wrapper">
            <div class="main-content__row">
                <div class="main-content__col main-content__col_1">
                    <ul class="payment-mode">
                        <payment-mode-item id="subscription" :title="$t('forms.payment_mode_subscription')" :disabled="$store.getters.effectPostInProgress" v-if="subscriptionModeSelectorVisible">
                            <div v-if="!$store.getters.authenticatedAsSubscriber && $store.getters.subscriptionsEnabled">
                                {{ $t('sponsor.no_subscription') }}
                                <subscription-levels />
                                <signin-with-google href="/login?provider=google" />
                            </div>
                            <signin-with-youtube href="/login?provider=youtube" v-else-if="!$store.getters.authenticatedWithYoutube && $store.getters.youtubeMembershipFreeEffectsAvailable && !$store.getters.subscriptionsEnabled" />
                            <div v-else-if="(!$store.getters.subscriber && $store.getters.subscriptionsEnabled) || !$store.getters.sponsor && $store.getters.youtubeMembershipFreeEffectsAvailable">
                                {{ $t('sponsor.no_subscription') }}
                                <subscription-levels />
                                <button class="btn-orange" @click="goToNewSubscription">
                                  {{ $t('sponsor.select_level') }}
                                </button>
                            </div>
                            <div v-else-if="!$store.getters.freeEffectsAvailable">
                                <router-link to="/subscriptions/select" v-if="$store.getters.subscriptionsEnabled">
                                    <span v-html="noFreeEffects" />
                                </router-link>
                                <span v-html="noFreeEffects" v-else />
                            </div>
                            <div v-else>
                                {{ $t('sponsor.limit_prefix') }}
                                <span v-for="(effectClass, index) in $store.getters.effectClasses" :key="effectClass.id"><span v-if="index > 0">, </span><span class="nowrap">{{effectClass.name}} - <span class="orange">{{$store.getters.limits[effectClass.id] || 0}}</span></span></span>,
                                {{ $t('sponsor.ai_sticker_creation') }} - <span class="orange">{{$store.getters.freeGenerationSessionsLeft}}</span>.
                                <div class="settings-row">
                                    <button class="btn-orange" @click="goToSubscriptions">
                                      {{ $t('subscriptions.settings') }}
                                    </button>
                                </div>
                            </div>
                        </payment-mode-item>
                        <payment-mode-item
                            v-for="method in $store.getters.allowedPaymentMethods" :key="method"
                            :id="method"
                            :title="$t('forms.payment_mode_' + method)"
                            :disabled="$store.getters.effectPostInProgress"
                            >
                            <amount-input :disabled="$store.getters.effectPostInProgress" />
                        </payment-mode-item>
                        <payment-mode-item
                            v-for="method in $store.getters.thirdPartyPaymentMethods" :key="method.id"
                            :id="method.id"
                            :title="method.text"
                            :disabled="$store.getters.effectPostInProgress"
                            >
                            <a class="btn-orange" :href="method.url">{{ $t('forms.initiate_payment') }}</a>
                        </payment-mode-item>
                        <payment-mode-item
                          v-if="$store.getters.freeEffectsEnabled || $store.getters.freeGenerationEnabled"
                          id="free"
                          :title="$store.getters.freeEffectsPaymentOptionName ? $store.getters.freeEffectsPaymentOptionName : $t('forms.free_effects_default_payment_option_name')"
                          :disabled="!canSendFreeEffect"
                        >
                          <span class="orange" v-if="secondsUntilNextFreeEffect > 0">{{ $t('forms.next_free_effect_available', {seconds: secondsUntilNextFreeEffect}) }}</span>
                          <div v-if="showFreeGenerationSessionNotice" class="notice-row">
                            <p>
                              {{ $t('forms.free_session_for_sticker_creation_found') }}
                              <router-link :to="generationURL" class="orange">
                                {{ $store.getters.haveUnspentAttempts ? $t('forms.you_can_create_a_sticker') : $t('forms.you_can_select_one_of_the_stickers') }}
                              </router-link>
                            </p>
                          </div>
                        </payment-mode-item>
                    </ul>
                </div>
                <div class="main-content__col main-content__col_2">
                    <EffectPostingForm v-if="$store.getters.paymentModeAvailable && !$store.getters.effectSelectorEnabled" />
                </div>
            </div>
            <div class="main-content__row" v-if="$store.getters.paymentModeAvailable && $store.getters.effectSelectorEnabled">
                <div class="main-content__col main-content__col_1">
                    <div class="title mt">{{ $t('forms.select_effect') }}</div>
                    <EffectList />
                </div>
                <div class="main-content__col main-content__col_2" v-if="selectedEffect">
                    <div class="title mt">{{ selectedEffect.name }}</div>
                    <EffectPostingForm />
                </div>
            </div>
        </div>
    </div>
</template>

<script>

import EffectList from "../components/EffectList";
import EffectPostingForm from "../components/EffectPostingForm";
import AmountInput from "../components/general/AmountInput";
import PaymentModeItem from "../components/PaymentModeItem";
import SubscriptionLevels from "../components/SubscriptionLevels";
import SignInWithYoutube from "../components/general/SigninWithYoutube.vue";
import SignInWithGoogle from "../components/general/SigninWithGoogle.vue";
import { mapMutations } from "vuex";
import SubscriptionStatus from "../store/models/SubscriptionStatus";
import {EffectGenerationSessionStatus} from "@/store/models/EffectGenerationSession";
import {getRequestedSponsorshipLevelID} from "@/sponsorshipLevelDeepLink";

export default {
    name: "SponsorForm",
    components: {
        EffectList,
        EffectPostingForm,
        AmountInput,
        PaymentModeItem,
        SigninWithYoutube: SignInWithYoutube,
        SigninWithGoogle: SignInWithGoogle,
        SubscriptionLevels,
    },
    data() {
        return {
            paymentMode: 'donation'
        };
    },
    computed: {
        showFreeGenerationSessionNotice() {
          return this.$store.getters.effectGenerationSession?.status === EffectGenerationSessionStatus.Free && this.$store.getters.secondsUntilNextFreeEffect > 0 && this.$store.getters.freeGenerationEnabled;
        },
        generationURL() {
          return `/generate?session=${this.$store.getters.effectGenerationSession?.uuid}`
        },
        secondsUntilNextFreeEffect() {
          return this.$store.getters.secondsUntilNextFreeEffect
        },
        canSendFreeEffect() {
            return !this.$store.getters.effectPostInProgress && this.$store.getters.secondsUntilNextFreeEffect <= 0 && (this.$store.getters.freeEffectsEnabled || this.$store.getters.freeGenerationEnabled);
        },
        effects() {
            let effects = {};
            for (let effect of this.$store.getters.effects) {
                effects[effect.id] = effect
            }
            return effects;
        },
        selectedEffect() {
            return this.effects[this.$store.getters.selectedEffect];
        },
        noFreeEffects() {
            let url = this.$store.getters.subscriptionsEnabled ? '/subscriptions/select' : this.$store.getters.joinUrl;
            if(this.$store.getters.currentSubscription?.status === SubscriptionStatus.Paid) {
              return url? this.$t('sponsor.no_effects', {'href': url}) : this.$t('sponsor.no_effects_without_url');
            }
            return this.$t('sponsor.no_active_subscription', {'href': url});
        },
        subscriptionModeSelectorVisible() {
            return this.$store.getters.subscriptionModeSelectorVisible;
        },
    },
    methods: {
        ...mapMutations(['setMode']),
        goToNewSubscription() {
          this.$router.push('/subscriptions/select')
        },
        goToSubscriptions() {
          this.$router.push('/subscriptions')
        },
        openDeepLinkedSponsorshipLevels() {
          if (
            getRequestedSponsorshipLevelID(this.$route)
            && this.subscriptionModeSelectorVisible
          ) {
            this.$store.dispatch('setPaymentMode', 'subscription');
          }
        }
    },
    watch: {
      subscriptionModeSelectorVisible(visible) {
        if (visible) {
          this.openDeepLinkedSponsorshipLevels();
        }
      },
      '$route.query.sponsorship_level'() {
        this.openDeepLinkedSponsorshipLevels();
      },
    },
    mounted() {
        this.setMode('sponsor');
        this.openDeepLinkedSponsorshipLevels();
    }
};
</script>
<style lang="scss">
.settings-row {
    margin-top: 16px;
}
.btn-orange {
    margin-left: 0;
    cursor: pointer;
}
.notice-row {
  margin-bottom: 20px;
  margin-top: 20px;
  p {
    margin: 0;
    line-height: 1.2;
  }
  a {
    cursor: pointer;
  }
}
</style>
